import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import CollectionViewModel from "../viewmodels/CollectionViewModel";

// The Collections tab: a list of curated albums. Each preview is the collection
// name in white on black (no thumbnail fetch — cheap and on-brand). Tapping one
// opens its images, which are fetched at that moment.
const CollectionsView = () => {
  const [collections, setCollections] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await CollectionViewModel.getCollections();
        if (!cancelled) setCollections(list);
      } catch (e) {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <Wrap>
        <Empty>Collections could not be loaded. Please try again later.</Empty>
      </Wrap>
    );
  }
  if (collections === null) {
    return (
      <Wrap>
        <Empty>Loading collections…</Empty>
      </Wrap>
    );
  }
  if (collections.length === 0) {
    return (
      <Wrap>
        <Empty>No collections yet. Check back soon.</Empty>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Grid>
        {collections.map((c) => (
          <Card key={c.id} to={`/collections/${c.id}`} aria-label={`Open the ${c.name} collection`}>
            <Preview>
              <PreviewName>{c.name}</PreviewName>
            </Preview>
            <Meta>
              <Name>{c.name}</Name>
              {c.description && <Description>{c.description}</Description>}
              <Count>
                {c.count} {c.count === 1 ? "image" : "images"}
              </Count>
            </Meta>
          </Card>
        ))}
      </Grid>
    </Wrap>
  );
};

export default CollectionsView;

const Wrap = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: var(--gap);
  width: 100%;
  box-sizing: border-box;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--gap);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: var(--paper);
`;

const Preview = styled.div`
  aspect-ratio: 4 / 3;
  background: #000;
  border-radius: var(--radius);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  transition: transform var(--dur) var(--ease), box-shadow var(--dur) var(--ease);

  ${Card}:hover & {
    transform: translateY(-4px);
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.45);
  }
  @media (hover: none) {
    ${Card}:hover & {
      transform: none;
    }
  }
`;

const PreviewName = styled.span`
  color: #fff;
  text-align: center;
  font-weight: bold;
  line-height: 1.2;
  letter-spacing: 0.02em;
  font-size: clamp(20px, 2.4vw, 30px);
`;

const Meta = styled.div`
  padding: 14px 4px 4px;
`;

const Name = styled.h2`
  margin: 0 0 6px;
  font-size: 18px;
  color: var(--paper);
`;

const Description = styled.p`
  margin: 0 0 8px;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Count = styled.span`
  color: var(--muted);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Empty = styled.p`
  color: var(--muted);
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
`;
